import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  createContext,
} from 'react';
import {
  getCarrierProfile,
  putCarrierProfileConditionsAccepted,
} from 'utils/network/http';
import { useAuth } from 'hooks/useAuth/useAuth';

export const DocsCarrierProfileContext = createContext();

export const ProvideDocsCarrierProfile = ({ children }) => {
  const carrierProfile = useProvideDocsCarrierProfile();
  return (
    <DocsCarrierProfileContext.Provider value={carrierProfile}>
      {children}
    </DocsCarrierProfileContext.Provider>
  );
};

export const useDocsCarrierProfile = () => {
  return useContext(DocsCarrierProfileContext);
};

const useProvideDocsCarrierProfile = () => {
  const [carrierProfile, setCarrierProfile] = useState({});
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [error, setError] = useState(false);
  const [carrierUuid, setCarrierUuid] = useState(null);
  const { profile } = useAuth();

  const getCarrierUuid = useCallback(() => {
    const uuid = profile?.carrier?.uuid;
    setCarrierUuid(uuid);
  }, [profile]);

  const clearCarrierProfile = useCallback(() => {
    setCarrierProfile({});
    setCarrierUuid(null);
  }, []);

  const fetchCarrierDocsProfile = useCallback(async () => {
    try {
      setLoadingProfile(true);
      setError(false);
      const { data } = await getCarrierProfile(carrierUuid);
      setCarrierProfile(data);
    } catch (e) {
      setError(true);
      console.error('Error fetchCarrierDocsProfile ', e);
    } finally {
      setLoadingProfile(false);
    }
  }, [carrierUuid]);

  const silentFetchCarrierDocsProfile = useCallback(async () => {
    try {
      setError(false);
      const { data } = await getCarrierProfile(carrierUuid);
      setCarrierProfile(data);
    } catch (e) {
      setError(true);
      console.error('Error fetchCarrierDocsProfile ', e);
    }
  }, [carrierUuid]);

  const patchCarrierDocsProfile = useCallback(async () => {
    try {
      setLoadingProfile(true);
      setError(false);
      const { data } = await putCarrierProfileConditionsAccepted(carrierUuid);
      setCarrierProfile(data);
    } catch (e) {
      setError(true);
      console.error('Error patchCarrierDocsProfile ', e);
    } finally {
      setLoadingProfile(false);
    }
  }, [carrierUuid]);

  useEffect(() => {
    if (
      carrierUuid &&
      Object.keys(carrierProfile).length === 0 &&
      !loadingProfile &&
      !error
    ) {
      fetchCarrierDocsProfile();
    }
  }, [
    fetchCarrierDocsProfile,
    carrierProfile,
    loadingProfile,
    carrierUuid,
    error,
  ]);

  useEffect(() => {
    if (!carrierUuid) {
      getCarrierUuid();
    }
  }, [carrierUuid, getCarrierUuid]);

  return {
    loadingProfile,
    carrierProfile,
    patchCarrierDocsProfile,
    error,
    silentFetchCarrierDocsProfile,
    carrierUuid,
    clearCarrierProfile,
  };
};

export default useDocsCarrierProfile;
